/**
 * Decision conflict API (FLOW-573 backend, surfaced by FLOW-577)
 *
 * `check` runs an Anthropic round trip on the backend, so — like
 * `draftDecision` — it borrows the chat timeout rather than the default fetch
 * behaviour. Resolving and reading stats are plain writes and reads.
 *
 * Every call here is project-scoped: the project comes from the
 * membership-checked URL path, never from a body field.
 */

import { api, ApiError } from "./client";
import type {
  ConflictCheckRequest,
  ConflictCheckResponse,
  ConflictStatsResponse,
  ConflictWarningId,
  ConflictWarningListParams,
  ConflictWarningListResponse,
  DecisionConflictWarning,
  ProjectId,
  ResolveConflictWarningRequest,
} from "./types";

/** Matches `decisions.ts`: the backend's Anthropic timeout plus headroom */
const CONFLICT_CHECK_TIMEOUT_MS = 130_000;

/**
 * Check a decision — saved or draft — against the project's existing
 * decisions. Persists one warning row per conflict found, so callers must not
 * re-check an unchanged subject: duplicates inflate the `shown` denominator
 * the acceptance rate is measured against.
 *
 * Returns 200 with `detection_status: "unavailable"` when no Anthropic client
 * is configured. That is an answer ("could not determine"), not an error.
 */
export async function checkDecisionConflicts(
  projectId: ProjectId,
  body: ConflictCheckRequest,
): Promise<ConflictCheckResponse> {
  const controller = new AbortController();
  const timeout = setTimeout(
    () => controller.abort(),
    CONFLICT_CHECK_TIMEOUT_MS,
  );

  try {
    return await api.post<ConflictCheckResponse>(
      `/projects/${projectId}/decision-conflicts/check`,
      body,
      { signal: controller.signal },
    );
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      throw new ApiError(408, "The request timed out. Please retry.");
    }
    throw err;
  } finally {
    clearTimeout(timeout);
  }
}

/** List warnings raised for a project, optionally filtered by outcome */
export async function listConflictWarnings(
  projectId: ProjectId,
  params: ConflictWarningListParams = {},
): Promise<ConflictWarningListResponse> {
  const search = new URLSearchParams();
  if (params.outcome) search.set("outcome", params.outcome);
  if (params.limit !== undefined) search.set("limit", String(params.limit));
  const qs = search.toString();

  return api.get<ConflictWarningListResponse>(
    `/projects/${projectId}/decision-conflicts${qs ? `?${qs}` : ""}`,
  );
}

/**
 * Record how a reader disposed of a warning. This write *is* the measurement.
 * A second resolve of the same warning is a 409 — the first opinion stands.
 */
export async function resolveConflictWarning(
  projectId: ProjectId,
  warningId: ConflictWarningId,
  body: ResolveConflictWarningRequest,
): Promise<DecisionConflictWarning> {
  return api.post<DecisionConflictWarning>(
    `/projects/${projectId}/decision-conflicts/${warningId}/resolve`,
    body,
  );
}

/** Acceptance-rate stats — the roadmap's Phase 8 exit criterion, as a number */
export async function getConflictStats(
  projectId: ProjectId,
): Promise<ConflictStatsResponse> {
  return api.get<ConflictStatsResponse>(
    `/projects/${projectId}/decision-conflicts/stats`,
  );
}
